import { useRef, useState } from "react";
import { api, API } from "@/lib/api";
import { useData } from "@/lib/data";
import { Button } from "@/components/ui/button";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription,
  AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { DatabaseBackup, Download, Upload, ShieldCheck } from "lucide-react";
import { toast } from "sonner";
import { AutoBackupList } from "@/components/AutoBackupList";
import { BackupEmailSettings } from "@/components/BackupEmailSettings";
import { LegacyCleanup } from "@/components/LegacyCleanup";
import { WeeklyReportCard } from "@/components/WeeklyReportCard";

export default function Backup() {
  const data = useData();
  const inputRef = useRef(null);
  const [file, setFile] = useState(null);
  const [restoring, setRestoring] = useState(false);
  const [result, setResult] = useState(null);

  const restore = async () => {
    const fd = new FormData();
    fd.append("file", file);
    setRestoring(true);
    try {
      const r = await api.post("/backup/restore", fd);
      setResult(r.data.counts);
      toast.success("Yedek geri yüklendi");
      await Promise.all(Object.keys(data).filter((k) => k.startsWith("refresh")).map((k) => data[k]?.()));
    } catch (e) {
      toast.error(e.response?.data?.detail || "Geri yükleme başarısız");
    } finally {
      setRestoring(false); setFile(null);
      if (inputRef.current) inputRef.current.value = "";
    }
  };

  return (
    <div className="p-5 lg:p-10 max-w-[1000px]" data-testid="backup-page">
      <p className="text-[11px] font-mono uppercase tracking-widest text-[#B45309] mb-2">Güvenlik</p>
      <h1 className="font-display text-3xl sm:text-4xl font-extrabold uppercase tracking-tight flex items-center gap-3">
        <DatabaseBackup className="w-7 h-7 text-[#D97706]" /> Yedekleme
      </h1>
      <p className="text-[#6B5B4A] mt-2 text-sm mb-8">Tüm verilerinizi Excel dosyası olarak indirin, gerektiğinde geri yükleyin.</p>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
        <section className="rounded-lg border border-[#DCCFB8] bg-[#FFFBF4] p-6" data-testid="backup-download-card">
          <Download className="w-6 h-6 text-[#B45309] mb-3" />
          <h2 className="font-display text-xl font-bold uppercase tracking-tight mb-2">Yedek İndir</h2>
          <p className="text-sm text-[#6B5B4A] mb-5">Müşteriler, kartlar, stok, tabanlar, kesimler, siparişler ve sevkiyatlar ayrı sayfalarda tek Excel dosyasına kaydedilir.</p>
          <a href={`${API}/backup`} data-testid="backup-download-btn">
            <Button className="w-full h-12 bg-[#D97706] hover:bg-[#B45309] text-white font-display uppercase tracking-wide">
              <Download className="w-4 h-4 mr-2" /> Excel Yedeği İndir
            </Button>
          </a>
        </section>

        <section className="rounded-lg border border-[#EF4444]/40 bg-[#FFFBF4] p-6" data-testid="backup-restore-card">
          <Upload className="w-6 h-6 text-[#EF4444] mb-3" />
          <h2 className="font-display text-xl font-bold uppercase tracking-tight mb-2">Yedekten Geri Yükle</h2>
          <p className="text-sm text-[#6B5B4A] mb-5">Programdaki mevcut verilerin <b>tamamı silinir</b> ve seçtiğiniz yedekteki veriler geri gelir.</p>
          <input ref={inputRef} type="file" accept=".xlsx" className="hidden" data-testid="backup-file-input"
            onChange={(e) => setFile(e.target.files?.[0] || null)} />
          <Button variant="outline" onClick={() => inputRef.current?.click()} disabled={restoring} data-testid="backup-restore-btn"
            className="w-full h-12 border-[#EF4444]/50 bg-transparent text-[#B91C1C] hover:bg-[#FDECEC] font-display uppercase tracking-wide">
            <Upload className="w-4 h-4 mr-2" /> {restoring ? "Yükleniyor..." : "Yedek Dosyası Seç"}
          </Button>
        </section>
      </div>

      <AutoBackupList />
      <BackupEmailSettings />
      <WeeklyReportCard />
      <LegacyCleanup />

      {result && (
        <section className="mt-5 rounded-lg border border-[#047857]/40 bg-[#ECF7F1] p-5" data-testid="backup-result">
          <p className="flex items-center gap-2 font-semibold text-[#047857] mb-3"><ShieldCheck className="w-5 h-5" /> Geri yükleme tamamlandı</p>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 text-sm">
            {Object.entries(result).map(([k, v]) => (
              <div key={k} className="rounded border border-[#DCCFB8] bg-[#FFFBF4] px-3 py-2">
                <p className="text-[10px] font-mono uppercase text-[#6B5B4A]">{k}</p>
                <p className="font-mono font-bold">{v}</p>
              </div>
            ))}
          </div>
        </section>
      )}

      <AlertDialog open={!!file} onOpenChange={(v) => { if (!v) { setFile(null); if (inputRef.current) inputRef.current.value = ""; } }}>
        <AlertDialogContent className="bg-[#FFFBF4] border-[#DCCFB8]" data-testid="backup-confirm-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>Yedek geri yüklensin mi?</AlertDialogTitle>
            <AlertDialogDescription>
              &ldquo;{file?.name}&rdquo; yüklenecek. Programdaki mevcut verilerin tamamı silinip yerine bu yedek gelecek. Bu işlem geri alınamaz.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="backup-confirm-cancel">Vazgeç</AlertDialogCancel>
            <AlertDialogAction onClick={restore} data-testid="backup-confirm-ok" className="bg-[#EF4444] hover:bg-[#B91C1C] text-white">
              Evet, geri yükle
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
